/**
 * Wspólne helpery + typy widoku kalendarza miesięcznego.
 *
 * Czysta logika daty (bez zależności od React ani od kształtu rekordu) — używana
 * przez `MonthGrid` (prezentacja) oraz oba adaptery danych: `CalendarView`
 * (rekordy modułu custom) i `CalendarBoard` (generyczne wiersze silnika list).
 * Bez zewnętrznych bibliotek (pure Date API); dzień wpisu z datą i godziną liczony w strefie
 * przeglądarki użytkownika (`toUserDateKey` z `@/lib/user-date-time`).
 */

import { toUserDateKey } from '@/lib/user-date-time';

/** Pojedynczy wpis kalendarza — dane już rozwiązane przez adapter (bez wiedzy o źródle). */
export interface CalendarEvent {
  /** Identyfikator wiersza — zwracany do `onEventClick`. */
  id: string;
  /** Tekst na pasku wydarzenia. */
  label: string;
  /**
   * Kolor tła paska: hex `#rrggbb` (opcje słowników) albo token palety `rgb(var(--token))`
   * (kolory zastępcze kalendarza). Kolor tekstu na pasku dobiera `getReadableTextColor`.
   */
  color: string;
  /** Dzień startu `YYYY-MM-DD` (pusty string dla wpisu bez daty → pasek „Brak daty"). */
  startKey: string;
  /** Dzień końca `YYYY-MM-DD` (== startKey dla pojedynczego dnia). */
  endKey: string;
}

export const WEEK_DAYS_PL = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Ndz'];

export const MONTHS_PL = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
];

/*
 * Kolory zastępcze kalendarza = tokeny palety aplikacji (`index.css`) w formacie
 * `rgb(var(--token))` — czytane wprost jako inline `backgroundColor`, bez konwersji. Dzięki temu
 * kalendarz nie ma własnej, rozjeżdżającej się z aplikacją palety.
 */

/**
 * Domyślny kolor wpisu, gdy brak skonfigurowanego pola statusu. `--color-info` (stały niebieski):
 * informacyjny, bliski dotychczasowemu niebieskiemu, a w odróżnieniu od `--accent-*` nie zależy
 * od motywu tenanta (domyślny akcent jest zielony i czytałby się jak stan „sukces").
 */
export const DEFAULT_ENTRY_COLOR = 'rgb(var(--color-info))';
/**
 * Kolor opcji statusu bez własnego koloru — neutralny szary (`--color-neutral`, stały token
 * niezależny od motywu i skórki).
 */
export const STATUS_FALLBACK_COLOR = 'rgb(var(--color-neutral))';
/**
 * Kolor wpisów w pasku „Brak daty" — wyciszony neutral (`--color-neutral-muted`, też stały),
 * jaśniejszy od szarego statusu.
 */
export const NO_DATE_COLOR = 'rgb(var(--color-neutral-muted))';

/** Kolor jako kanały `[r, g, b]` w zakresie 0..255. */
export type Rgb = readonly [number, number, number];

/** Biały tekst na ciemnym tle wpisu (dotychczasowy domyślny kolor tekstu). */
const TEXT_ON_DARK_RGB: Rgb = [255, 255, 255];
/**
 * Ciemny tekst na jasnym tle wpisu. STAŁA wartość (gray-900 = `--text-primary` motywu jasnego),
 * a nie token: `--text-primary` w trybie ciemnym jest jasny, tymczasem tła wpisów to stałe tokeny
 * palety (`--color-warning/-info/-success/-error/-neutral*`) albo hex z danych (opcje słowników)
 * — żadne z nich nie zależy od motywu, więc tekst zależny od motywu znikałby na nich po
 * przełączeniu na ciemny.
 */
const TEXT_ON_LIGHT_RGB: Rgb = [17, 24, 39];

function toCssRgb([r, g, b]: Rgb): string {
  return `rgb(${r}, ${g}, ${b})`;
}

/** Kolor tekstu dla ciemnych teł wpisów (biały). */
export const ENTRY_TEXT_ON_DARK = toCssRgb(TEXT_ON_DARK_RGB);
/** Kolor tekstu dla jasnych teł wpisów (ciemny). */
export const ENTRY_TEXT_ON_LIGHT = toCssRgb(TEXT_ON_LIGHT_RGB);

function toRgb(r: number, g: number, b: number): Rgb | null {
  return [r, g, b].every((c) => Number.isInteger(c) && c >= 0 && c <= 255) ? [r, g, b] : null;
}

/** `#rgb` lub `#rrggbb`. */
function parseHexColor(value: string): Rgb | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
  const digits = match?.[1];
  if (!digits) return null;
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((c) => c + c)
          .join('')
      : digits;
  return toRgb(
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  );
}

/** Trójka kanałów `245 184 65` (format tokenów z `index.css`) lub `245, 184, 65`. */
function parseChannelTriplet(value: string): Rgb | null {
  const match = /^(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})$/.exec(value.trim());
  if (!match) return null;
  return toRgb(Number(match[1]), Number(match[2]), Number(match[3]));
}

/** Wartość zmiennej CSS z korzenia dokumentu (stałe tokeny palety są zdefiniowane na `:root`). */
function readRootCssVariable(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value === '' ? null : value;
}

/**
 * Parsuje kolor tła wpisu do RGB. Obsługiwane formaty: `#rgb`/`#rrggbb`, `rgb(r g b)`,
 * `rgb(r, g, b)` oraz token palety `rgb(var(--token))` (rozwiązywany w czasie działania z
 * korzenia dokumentu; tokeny teł kalendarza są stałe, nie zależą od motywu). Zwraca `null`, gdy
 * kolor jest nieparsowalny albo zmienna nie istnieje.
 */
export function parseCssColor(value: string): Rgb | null {
  const input = value.trim();
  if (input.startsWith('#')) return parseHexColor(input);

  const token = /^rgb\(\s*var\(\s*(--[\w-]+)\s*\)\s*\)$/i.exec(input);
  if (token?.[1]) {
    const resolved = readRootCssVariable(token[1]);
    return resolved ? parseChannelTriplet(resolved) : null;
  }

  const plain = /^rgb\(\s*([^()]+?)\s*\)$/i.exec(input);
  return plain?.[1] ? parseChannelTriplet(plain[1]) : null;
}

/** Luminancja względna wg WCAG 2.x (0 = czerń, 1 = biel). */
function relativeLuminance([r, g, b]: Rgb): number {
  const linear = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** Współczynnik kontrastu WCAG między dwiema luminancjami (1..21). */
function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

const LUMINANCE_ON_DARK = relativeLuminance(TEXT_ON_DARK_RGB);
const LUMINANCE_ON_LIGHT = relativeLuminance(TEXT_ON_LIGHT_RGB);

/**
 * Kolor tekstu czytelny na danym tle wpisu: ciemny na jasnym, biały na ciemnym — wygrywa ten
 * z dwóch, który daje wyższy kontrast WCAG z tłem (decyzja właścicielki 01.10; wcześniej tekst
 * był zawsze biały, nieczytelny na żółtym „Nowy"). Przy tle, którego nie da się sparsować ani
 * rozwiązać (nieznany format, brak zmiennej CSS), zostaje dotychczasowy biały tekst.
 */
export function getReadableTextColor(background: string): string {
  const rgb = parseCssColor(background);
  if (!rgb) return ENTRY_TEXT_ON_DARK;
  const luminance = relativeLuminance(rgb);
  return contrastRatio(luminance, LUMINANCE_ON_LIGHT) > contrastRatio(luminance, LUMINANCE_ON_DARK)
    ? ENTRY_TEXT_ON_LIGHT
    : ENTRY_TEXT_ON_DARK;
}

/** Zwraca 42 komórki (6 × 7) zaczynając od poniedziałku; null = padding sąsiedniego miesiąca. */
export function buildCalendarGrid(year: number, month: number): Array<Date | null> {
  const firstDay = new Date(year, month, 1);
  // Dzień tygodnia: 0=niedziela → mapujemy na pn=0, ..., ndz=6
  const startDow = (firstDay.getDay() + 6) % 7;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const grid: Array<Date | null> = [];
  for (let i = 0; i < startDow; i++) grid.push(null);
  for (let d = 1; d <= lastDay; d++) grid.push(new Date(year, month, d));
  while (grid.length < 42) grid.push(null);
  return grid;
}

/** `YYYY-MM-DD` z Date (lokalny czas, nie UTC). */
export function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Dzień `YYYY-MM-DD` z wartości pola: data bez godziny bez zmian, data z godziną — dzień
 * w strefie przeglądarki (w Polsce termin 5.11 0:30 stoi 5.11, choć w UTC to jeszcze 4.11). Zwraca null gdy
 * brak/nieparsowalne.
 */
export function extractDateStr(val: unknown): string | null {
  if (!val || typeof val !== 'string') return null;
  return toUserDateKey(val);
}

/** Parsuje `YYYY-MM-DD` na lokalną datę (północ lokalna, nie UTC). */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/**
 * Grupuje wpisy po dniu w obrębie widocznego miesiąca. Wpis z zakresem [start, end]
 * trafia do każdego dnia zakresu przecinającego się z miesiącem (bez sztucznego
 * limitu — miesiąc = najwyżej ~31 iteracji na wpis). Koniec < start → pojedynczy dzień.
 * Wpisy bez daty (`startKey === ''`) są pomijane (renderowane osobno w pasku „Brak daty").
 */
export function groupEventsByDate(
  events: CalendarEvent[],
  year: number,
  month: number,
): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  for (const ev of events) {
    if (!ev.startKey) continue;
    const endStr = ev.endKey && ev.endKey >= ev.startKey ? ev.endKey : ev.startKey;
    const rangeStart = parseLocalDate(ev.startKey);
    const rangeEnd = parseLocalDate(endStr);
    let cursor = rangeStart > monthStart ? rangeStart : monthStart;
    const last = rangeEnd < monthEnd ? rangeEnd : monthEnd;
    while (cursor <= last) {
      const key = toLocalDateStr(cursor);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
    }
  }
  return map;
}
